<template>
  <el-dialog
    title="选择才艺视频"
    :visible.sync="dialogVisible"
    width="760px"
    append-to-body
    class="talent-video-picker-dialog"
    @open="handleOpen"
    @closed="handleClosed"
  >
    <div class="picker-toolbar">
      <el-input
        v-model="keyword"
        clearable
        placeholder="搜索才艺视频"
        prefix-icon="el-icon-search"
        @keyup.enter.native="handleSearch"
        @clear="handleSearch"
      />
      <el-button type="primary" @click="handleSearch">搜索</el-button>
    </div>

    <div v-loading="loading" class="picker-body">
      <div v-if="list.length" class="video-grid">
        <div
          v-for="item in list"
          :key="item.id"
          class="video-card"
          :class="{ active: String(selectedId) === String(item.id) }"
          @click="handleSelect(item)"
        >
          <div class="video-card-cover-wrap">
            <img
              v-if="item.coverUrl"
              class="video-card-cover"
              :src="resolveMediaUrl(item.coverUrl)"
              alt=""
            >
            <div v-else class="video-card-cover video-card-cover-empty">无封面</div>
            <span class="video-card-play">
              <i class="el-icon-caret-right" />
            </span>
          </div>
          <div class="video-card-title">{{ item.title || '未命名视频' }}</div>
          <div class="video-card-meta">
            <span class="video-card-artist">{{ item.artistName || '艺人' }}</span>
            <span class="video-card-taxonomy">{{ item.taxonomyName || '-' }}</span>
          </div>
        </div>
      </div>
      <el-empty v-else description="暂无视频" :image-size="88" />
    </div>

    <div class="picker-footer">
      <el-pagination
        v-if="total > pageSize"
        background
        layout="prev, pager, next"
        :current-page.sync="page"
        :page-size="pageSize"
        :total="total"
        @current-change="fetchList"
      />
      <div v-else-if="list.length" class="picker-footer-tip">已展示全部</div>
    </div>
  </el-dialog>
</template>

<script>
import { getPlatformTalentVideosPage } from '../../api/platformManage'
import { resolveMediaUrl } from '../../utils/format'

const PAGE_SIZE = 10

export default {
  name: 'TalentVideoPickerDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    selectedId: {
      type: [String, Number],
      default: ''
    },
    taxonomyId: {
      type: [String, Number],
      default: ''
    },
    artistUserId: {
      type: [String, Number],
      default: ''
    }
  },
  data() {
    return {
      keyword: '',
      list: [],
      loading: false,
      page: 1,
      pageSize: PAGE_SIZE,
      total: 0
    }
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible
      },
      set(value) {
        this.$emit('update:visible', value)
      }
    }
  },
  methods: {
    resolveMediaUrl,
    handleOpen() {
      this.page = 1
      this.fetchList()
    },
    handleClosed() {
      this.keyword = ''
      this.list = []
      this.page = 1
      this.total = 0
    },
    handleSearch() {
      this.page = 1
      this.fetchList()
    },
    async fetchList() {
      this.loading = true
      try {
        const params = {
          keyword: (this.keyword || '').trim(),
          shelfStatus: 1,
          page: this.page,
          pageSize: this.pageSize
        }
        if (this.artistUserId) {
          params.artistUserId = this.artistUserId
        }
        if (this.taxonomyId) {
          params.taxonomyId = this.taxonomyId
        }
        const data = await getPlatformTalentVideosPage(params)
        this.list = (data && data.records) || []
        this.total = Number((data && data.total) || 0)
        this.page = Number((data && data.page) || this.page)
      } catch (error) {
        this.list = []
        this.total = 0
      } finally {
        this.loading = false
      }
    },
    handleSelect(item) {
      this.$emit('select', item)
      this.dialogVisible = false
    }
  }
}
</script>

<style scoped>
.picker-toolbar {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
}

.picker-toolbar .el-input {
  flex: 1;
}

.picker-body {
  min-height: 280px;
}

.video-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.video-card {
  border: 1px solid #ebeef5;
  border-radius: 10px;
  background: #fff;
  padding: 10px;
  cursor: pointer;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.video-card:hover,
.video-card.active {
  border-color: #409eff;
  box-shadow: 0 4px 12px rgba(64, 158, 255, 0.12);
}

.video-card-cover-wrap {
  position: relative;
  border-radius: 8px;
  overflow: hidden;
}

.video-card-cover {
  display: block;
  width: 100%;
  height: 148px;
  object-fit: cover;
  background: #f5f7fa;
}

.video-card-cover-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #909399;
  font-size: 13px;
}

.video-card-play {
  position: absolute;
  right: 10px;
  bottom: 10px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.92);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #303133;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
}

.video-card-play i {
  margin-left: 2px;
}

.video-card-title {
  margin-top: 10px;
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.video-card-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 8px;
  font-size: 13px;
}

.video-card-artist {
  color: #909399;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.video-card-taxonomy {
  flex-shrink: 0;
  color: #409eff;
}

.picker-footer {
  margin-top: 16px;
  min-height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.picker-footer-tip {
  color: #909399;
  font-size: 13px;
}
</style>
